/**
 * Shared component schemas referenced across all layers via
 * `$ref: '#/components/schemas/<Name>'`.
 */

// Per-level odds block reused by the three volatility levels (normal/high/max)
// in every `odds_levels` object (Player API game list/detail, Operator API game
// list). Same shape as the top-level `odds` + value stats. Inlined by the
// importing paths, not registered under `schemas`.
export const oddsLevelSchema = {
  type: 'object',
  title: 'OddsLevel',
  required: ['tiers', 'min_value', 'max_value', 'big_win_chance'],
  properties: {
    tiers: {
      type: 'array',
      description: 'Per-tier odds for this volatility level, sorted from the lowest value tier upward.',
      items: {
        type: 'object',
        title: 'OddsTier',
        required: ['tier_name', 'range', 'percentage'],
        properties: {
          tier_name: { type: 'string', description: 'Tier label, e.g. "Common", "Uncommon", "Legendary".' },
          range: { type: 'string', description: 'Formatted value range for the tier, e.g. "$0 - $50" or "$500+".' },
          percentage: { type: 'number', description: 'Probability of pulling a card in this tier, as a percentage.' },
        },
      },
    },
    min_value: { type: 'number', description: 'Lowest possible card value in USD at this level.' },
    max_value: { type: 'number', description: 'Highest tier floor value in USD at this level.' },
    big_win_chance: { type: 'number', description: 'Chance, as a percentage, of pulling a card above the lowest tier at this level.' },
  },
};

export const schemas = {
  /** REST-style error body returned by the Player + Operator APIs. */
  ErrorEnvelope: {
    type: 'object',
    required: ['error', 'error_detail'],
    properties: {
      error: { type: 'integer', enum: [1], description: 'Non-zero marks a failure.' },
      error_detail: {
        type: 'object',
        required: ['id', 'code', 'message'],
        properties: {
          id: { type: 'integer', example: 101, description: 'Numeric error id.' },
          code: {
            type: 'string',
            description:
              'Machine-readable error code. Catalog codes (which carry a numeric `id`) are listed in the ' +
              'table below; some endpoints also return endpoint-specific codes (e.g. `INVALID_TEMPLATE`, ' +
              '`PURCHASE_FAILED`, `IP_NOT_WHITELISTED`) documented on the operation itself.',
            example: 'INVALID_TOKEN',
          },
          message: { type: 'string', example: 'Invalid or expired session token' },
        },
      },
    },
    description: [
      'The same catalog is used in both directions: these are the codes we return to you, and the codes',
      'your wallet callbacks return to us. *What to do* is written for whoever receives the error.',
      '',
      '| id | code | HTTP | meaning | what to do |',
      '|----|------|------|---------|------------|',
      '| 1001 | `INVALID_API_KEY` | 401 | Invalid API key | Check the key — it is per platform and shown to the console owner only. |',
      '| 1003 | `INVALID_SIGNATURE` | 401 | Invalid request signature | Re-check the signing string: method, path *with* query, the exact body bytes, and the secret. |',
      '| 1004 | `EXPIRED_TIMESTAMP` | 401 | Timestamp outside ±5 min skew | Sync the server clock (NTP). |',
      '| 1005 | `RATE_LIMITED` | 429 | Too many requests | Back off and retry with an increasing delay. |',
      '| 1006 | `IP_BANNED` | 403 | Source IP temporarily banned after repeated auth failures | Fix the credentials first; the ban lifts by itself. |',
      '| — | `IP_NOT_WHITELISTED` | 403 | Source IP not on the operator IP whitelist | Add the address under Integration → IP whitelist. |',
      '| 101 | `INVALID_TOKEN` | 401 | Invalid/expired session token | Create a new session and reload the player UI — *Handling guide → Session expired*. |',
      '| 2041 | `DUPLICATE_TRANSACTION_ID` | 409 | Duplicate transaction_id (treated as success on callbacks) | Already processed — treat as success, do not retry. |',
      '| 2033 | `CURRENCY_MISMATCH` | 422 | Currency mismatch | Use the session currency; it is fixed at Create session. |',
      '| 2028 | `INSUFFICIENT_BALANCE` | 422 | Insufficient balance | Your wallet returns it from `/debit`; as a Player API error, send the player to your cashier — *Handling guide → Insufficient balance*. |',
      '| — | `SPENDING_LIMIT_REACHED`, `SPENDING_LIMIT_REACHED_<PERIOD>` | 422 | Your own code, passed through | Your wallet returns it from `/debit` for a player spending limit; `message` is your `error_message` (or a default), ready to show — *Handling guide → Spending limit reached*. |',
      '| 2035 | `FAILED_TRANS` | 422 | Transaction failed | Retry once with the same `transaction_id`; if it persists, check the callback log in the console. |',
      '| 2034 | `ZERO_AMOUNT_VALUE` | 422 | Zero amount not allowed | Caller-side bug — nothing to retry. |',
      '| 2027 | `NEGATIVE_AMOUNT_VALUE` | 422 | Negative amount not allowed | Caller-side bug — nothing to retry. |',
      '| 2037 | `INVALID_TIME_INTERVAL` | 422 | Invalid date range (history / summary window) | Fix or narrow the from/to window. |',
      '| 2022 | `INVALID_GAME_ID` | 422 | Invalid game_id | Use an id from **List available games**. |',
      '| 2021 | `INVALID_PLAYER_ID` | 422 | Invalid player_id | Use the `account_id` your `get_session` returned. |',
      '| 2094 | `INVALID_STATUS` | 422 | Unknown `status` filter value | Use only the values listed for that endpoint — the message names them. |',
      '| 2098 | `SHIPPING_NOT_CANCELABLE` | 422 | Delivery cancel window closed (fulfillment started) | Too late to cancel — nothing to retry. |',
      '| 2097 | `SHIPPING_CANCEL_FAILED` | 422 | Delivery cancel failed — transient, safe to retry | Retry. |',
      '| 2096 | `UNSUPPORTED_TRANSACTION` | 422 | You do not settle this payout type — permanent, we stop retrying | Return it only for payout types you never settle. |',
      '| 2095 | `SHIPPING_ITEM_LIMIT_REACHED` | 422 | The player already has your maximum number of cards awaiting fulfillment | Temporary: it clears as those deliveries ship. Read `remaining_items` from **List shipping options** and cap the card picker. |',
      '| 2100 | `QUANTITY_LIMIT_EXCEEDED` | 422 | `quantity` is above your operator-configured maximum for a single order | Nothing is charged. Lower the quantity and retry — contact us if you need the maximum raised. |',
      '| — | `ADDRESS_IN_USE` | 422 | The address is used by a delivery that is not delivered or canceled yet | Nothing is deleted. Let the player delete it once that delivery completes. |',
      '| 4040 | `NOT_FOUND` | 404 | Resource not found | Check the id and that the resource belongs to your platform. |',
    ].join('\n'),
  },

  /** Provably fair: the signed commitment issued before a purchase (protocol pf-a-v1). */
  FairnessTicket: {
    type: 'object',
    title: 'FairnessTicket',
    description:
      'Everything the rarity roll of the next purchase depends on, fixed and signed by Repackz **before** the ' +
      'player seed exists. Show `server_seed_hash` to the player before the purchase. See *Provably fair* in the ' +
      'overview for the exact byte layout.',
    required: [
      'id', 'proto', 'pool_offer_id', 'server_seed_hash', 'odds', 'odds_digest', 'params_fingerprint',
      'max_quantity', 'issued_at_ms', 'valid_until_ms', 'signing_key_id', 'signature',
    ],
    properties: {
      id: { type: 'string', pattern: '^[0-9a-f]{32}$', description: 'Ticket id (128 random bits). Also the id of its public receipt.' },
      proto: { type: 'string', enum: ['pf-a-v1'], description: 'Protocol version.' },
      pool_offer_id: { type: 'integer', description: 'The game (pack) the ticket is for.' },
      server_seed_hash: { type: 'string', pattern: '^[0-9a-f]{64}$', description: 'SHA-256 of our 32-byte server seed (hex). The seed itself is revealed after the purchase.' },
      odds: {
        type: 'object',
        description:
          'The odds per volatility level (`normal`, `high`, `max`; packs with fixed odds have `normal` only). ' +
          'Each level lists the rarity tiers from the lowest value up, with integer `ticks` summing to 100 000 000.',
        additionalProperties: {
          type: 'array',
          items: {
            type: 'object',
            required: ['tier', 'ticks'],
            properties: {
              tier: { type: 'string', description: 'Tier key: a fair-market-value range in USD, `from..to` (`250..Infinity` for the open top tier).', example: '50..100' },
              ticks: { type: 'integer', minimum: 0, description: 'Chance of the tier in units of 1 / 100 000 000.', example: 18000000 },
            },
          },
        },
      },
      odds_digest: { type: 'string', pattern: '^[0-9a-f]{64}$', description: 'SHA-256 commitment to `odds` (hex).' },
      params_fingerprint: { type: 'string', pattern: '^[0-9a-f]{64}$', description: 'SHA-256 commitment to the purchase parameters: pack price, value settings and the maximum quantity (hex). Verifiers use it as-is.' },
      max_quantity: { type: 'integer', description: 'The most packs one purchase with this ticket may buy.' },
      issued_at_ms: { type: 'integer', description: 'When the ticket was issued, Unix ms.' },
      valid_until_ms: { type: 'integer', description: 'The purchase must reach us before this moment, Unix ms (about 90 s after issue).' },
      signing_key_id: { type: 'string', pattern: '^[0-9a-f]{16}$', description: 'Which published key signed the ticket: first 16 hex characters of SHA-256 over the key’s SubjectPublicKeyInfo DER.' },
      signature: { type: 'string', pattern: '^[0-9a-f]{128}$', description: 'ECDSA P-256 / SHA-256 signature as raw `r ‖ s` hex — the form WebCrypto verifies directly.' },
    },
  },

  /** Provably fair: what a purchase can be verified with afterwards. */
  FairnessReceipt: {
    type: 'object',
    title: 'FairnessReceipt',
    description:
      'A provably-fair purchase: the signed ticket, both seeds and each card slot’s roll. Returned in ' +
      '`pool_order.fairness` and by **Get a receipt**. `server_seed` is null until nothing can change the ' +
      'outcome any more (`state: dead`).',
    required: ['proto', 'state', 'ticket', 'client_seed', 'volatility', 'quantity', 'server_seed', 'slots'],
    properties: {
      proto: { type: 'string', enum: ['pf-a-v1'] },
      state: {
        type: 'string',
        enum: ['issued', 'consumed', 'dead'],
        description: '`issued`: not used yet. `consumed`: a purchase is running with it. `dead`: finished, refused or expired — the server seed is revealed.',
      },
      ticket_id: { type: 'string', description: 'Purchase responses only: same as `ticket.id`.' },
      ticket: { $ref: '#/components/schemas/FairnessTicket' },
      client_seed: { type: 'string', nullable: true, pattern: '^[0-9a-fA-F]{64}$', description: 'The player seed sent with the purchase; null for a ticket never used.' },
      volatility: { type: 'string', nullable: true, enum: ['normal', 'high', 'max', null], description: 'The volatility level the purchase used — which `odds` list the roll reads.' },
      quantity: { type: 'integer', nullable: true, description: 'Packs in the purchase.' },
      server_seed: { type: 'string', nullable: true, pattern: '^[0-9a-f]{64}$', description: 'Our 32-byte server seed (hex), once `state` is `dead`.' },
      slots: {
        type: 'array',
        description: 'One entry per card slot of the purchase, 0-based.',
        items: {
          type: 'object',
          required: ['slot', 'provably_fair'],
          properties: {
            slot: { type: 'integer', description: 'Card slot, 0-based.' },
            roll: { type: 'integer', nullable: true, minimum: 0, maximum: 99999999, description: 'The rarity roll, 0 … 99 999 999.' },
            tier: { type: 'string', nullable: true, description: 'The tier the roll landed in.' },
            provably_fair: { type: 'boolean', description: 'false for a jackpot card or a promotional award: no roll took place, nothing to verify.' },
          },
        },
      },
      pack: {
        type: 'object',
        description: 'Get a receipt only.',
        properties: { id: { type: 'integer' }, title: { type: 'string', nullable: true } },
      },
    },
  },

  /** Pagination block on list endpoints. */
  PaginationMeta: {
    type: 'object',
    required: ['total_count', 'page', 'per'],
    properties: {
      total_count: { type: 'integer', example: 12, description: 'Total matching rows across all pages.' },
      page: { type: 'integer', example: 1, description: 'Echoed current page (1-based).' },
      per: { type: 'integer', example: 32, description: 'Echoed page size (default 32, max 100).' },
    },
  },

  /** Image object embedded in several payloads. */
  ImageObject: {
    type: 'object',
    nullable: true,
    properties: {
      url: { type: 'string', format: 'uri', example: 'https://cdn.getcardbase.com/blobs/pack_463.png' },
      metadata: { type: 'object', additionalProperties: true, example: { width: 600, height: 800 } },
      signed_id: { type: 'string', example: 'eyJfcmFpbHMiOnsi...' },
    },
  },

  /**
   * Structured catalog enrichment for a card, returned only when the request
   * asks for it with `include_card_details=true`. Free-form by design: the key
   * set differs per card category (e.g. Pokémon vs Sport Card).
   */
  CardDetails: {
    type: 'object',
    title: 'CardDetails',
    nullable: true,
    additionalProperties: true,
    description: [
      'Structured catalog enrichment for the card — set, rarity, player or character, grade and related attributes.',
      '',
      'The exact set of keys depends on the card category: Pokémon cards and Sport Card cards expose different fields, so treat this as a free-form object and read only the keys you need.',
      '',
      'Enrichment runs asynchronously after a card is created, so this is null until it completes.',
      'Only present in the response when the request is sent with include_card_details=true.',
    ].join('\n'),
    examples: [
      {
        title: '2023 Panini Prizm LeBron James #1',
        card: {
          set: 'Panini Prizm',
          slab_year: 2023,
          card_number: '1',
          player: 'LeBron James',
          team: 'Los Angeles Lakers',
          rookie: false,
          serial_number: '12/25',
          grade: { company: 'PSA', grade: '10' },
        },
        attributes: {
          sport: 'Basketball',
          variation: 'Silver Prizm',
          autograph: false,
          memorabilia: false,
        },
      },
      {
        name: 'Charizard',
        category: 'Pokémon',
        artist_illustrator: 'Mitsuhiro Arita',
        language: 'English',
        pokemon: {
          character: 'Charizard',
          series: 'Base Set',
          set: 'Base Set',
          card_number: '4/102',
          rarity: 'Holo Rare',
          hp: 120,
          type: 'Fire',
          stage: 'Stage 2',
        },
      },
    ],
  },
} as const;

/**
 * Shared, reusable operation parameters referenced via
 * `$ref: '#/components/parameters/<Name>'`.
 */
export const parameters = {
  /** Opt-in query flag that adds the `card_details` enrichment to each card. */
  IncludeCardDetails: {
    name: 'include_card_details',
    in: 'query',
    required: false,
    schema: { type: 'boolean', default: false },
    description: [
      'When true, each card in the response includes the structured card_details enrichment object.',
      'When false or omitted, the card_details key is not present.',
    ].join('\n'),
  },
  /** Opt-in query flag that populates each game's cached headline card in `example_item`. */
  IncludeExampleItem: {
    name: 'include_example_item',
    in: 'query',
    required: false,
    schema: { type: 'boolean', default: false },
    description: [
      "When true, each game in the response carries its headline card in `example_item` — the most valuable card currently pullable within the pack's odds tiers (picked by the same rule as **List most expensive cards** with `game_id=<id>&limit=1`), with `title`, `image_url` and, when `include_card_details=true` is also passed, `card_details`.",
      'The value is computed in the background and cached: an entry is refreshed once it is 15 minutes old, so it can lag the live pool by roughly 15 minutes. It is null while a pack is still warming up (on the first poll that asks for it, or after 24 hours without one — the next poll usually has it) or when the pack has no eligible card.',
      'When false or omitted, `example_item` is null and nothing is computed, so lobbies that do not render it pay nothing.',
    ].join('\n'),
  },
  /** Opt-in query flag that adds the per-volatility `odds_levels` breakdown to each game. */
  WithOddsLevels: {
    name: 'with_odds_levels',
    in: 'query',
    required: false,
    schema: { type: 'boolean', default: false },
    description: [
      'When true, each game in the response includes the per-volatility `odds_levels` object (Normal / High / Max).',
      'When false or omitted, the odds_levels key is not present.',
    ].join('\n'),
  },
} as const;
